import { UserPermission } from '@cobliteam/common-types';
import { Location, locationUserPermission } from '@cobliteam/common-types/src/location';
import Icon from '@cobliteam/ui/icons';
import type { SidebarDef } from './AppSidebar.d';

/**
 *  Nav -> Section -> Group -> Item
 */

const sidebarDef = ({ hasCameraFlag, hasRedeCan }: { hasCameraFlag?: boolean; hasRedeCan?: boolean }): SidebarDef[] => [
  {
    title: 'Início',
    url: Location.HOME,
    icon: <Icon name='house' />,
    permission: locationUserPermission[Location.HOME],
  },
  {
    title: 'Operação',
    icon: <Icon name='map-pin' />,
    permission: UserPermission.BASIC_PERMISSION,
    section: [
      {
        title: 'Monitoramento',
        icon: <Icon name='map' />,
        url: Location.MONITORING,
        permission: locationUserPermission[Location.MONITORING],
      },
      {
        title: 'Trajetos',
        icon: <Icon name='route' />,
        url: Location.TRACKING_HISTORY,
        permission: locationUserPermission[Location.TRACKING_HISTORY],
      },
      {
        title: 'Serviços e entregas',
        icon: <Icon name='package' />,
        url: Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER,
        permission: locationUserPermission[Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER],
      },
      {
        title: 'Documentos',
        icon: <Icon name='files' />,
        permission: locationUserPermission[Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER],
        group: [
          {
            title: 'Comprovantes',
            url: Location.PROOF_OF_CONCLUSION,
            permission: locationUserPermission[Location.PROOF_OF_CONCLUSION],
          },
          {
            title: 'Checklists',
            url: Location.CHECKLIST,
            redirect: Location.CHECKLIST_DASHBOARD,
            permission: locationUserPermission[Location.CHECKLIST],
          },
        ],
      },
      {
        title: 'Política de Frota',
        icon: <Icon name='gavel' />,
        url: Location.FLEET_POLICY,
        permission: locationUserPermission[Location.FLEET_POLICY],
      },
    ],
  },
  {
    title: 'Segurança',
    icon: <Icon name='shield-alert' />,
    permission: UserPermission.BASIC_PERMISSION,
    section: [
      {
        title: 'Cobli Cam',
        icon: <Icon name='video' />,
        url: hasCameraFlag ? Location.CAMERA.replace('/:type', '') : Location.CAMERA_LP,
        permission: locationUserPermission[Location.CAMERA],
        group: hasCameraFlag
          ? [
              {
                title: 'Eventos de vídeo',
                url: Location.CAMERA_EVENTS,
                permission: locationUserPermission[Location.CAMERA_EVENTS_DASHBOARD],
              },
              {
                title: 'Vídeos solicitados',
                url: Location.CAMERA_REQUESTS,
                permission: locationUserPermission[Location.CAMERA_REQUESTS],
              },
              {
                title: 'Identificação facial',
                url: Location.CAMERA_TRIP_IMAGE_HISTORY,
                permission: locationUserPermission[Location.CAMERA_TRIP_IMAGE_HISTORY],
              },
              {
                title: 'Saúde das câmeras',
                url: Location.CAMERA_HEALTH_OBSTRUCTION,
                permission: locationUserPermission[Location.CAMERA_HEALTH_OBSTRUCTION],
                featureFlag: 'showObstructionDashboard',
              },
              {
                title: 'Vídeo Wall',
                url: Location.LIVESTREAM,
                permission: locationUserPermission[Location.LIVESTREAM],
                featureFlag: 'livestreamPage',
              },
            ]
          : undefined,
      },
      {
        title: 'Visão geral',
        icon: <Icon name='shield' />,
        url: Location.SAFETY_RANKING_DASHBOARD,
        permission: locationUserPermission[Location.SAFETY_RANKING_DASHBOARD_DRIVER],
        featureFlag: 'safetyRankingDash',
        redirect: Location.SAFETY_RANKING_DASHBOARD_DRIVER,
      },
      {
        title: 'Ranking de condução',
        icon: <Icon name='list-ordered' />,
        url: Location.SAFETY_RANKING,
        permission: locationUserPermission[Location.SAFETY_RANKING_VEHICLE],
        redirect: Location.SAFETY_RANKING_VEHICLE,
      },
      {
        title: 'Velocidade',
        icon: <Icon name='gauge' />,
        url: Location.SPEEDING,
        permission: locationUserPermission[Location.SPEEDING],
      },
    ],
  },
  {
    title: 'Eficiência',
    icon: <Icon name='trending-up' />,
    permission: UserPermission.BASIC_PERMISSION,
    section: [
      {
        title: 'Condução econômica',
        icon: <Icon name='traffic-cone' />,
        url: Location.ECONOMIC_DRIVING,
        permission: locationUserPermission[Location.ECONOMIC_DRIVING],
        hide: !hasRedeCan,
      },
      {
        title: 'Produtividade',
        icon: <Icon name='trending-up' />,
        url: Location.PRODUCTIVITY,
        permission: locationUserPermission[Location.PRODUCTIVITY],
      },
      {
        title: 'Gastos',
        icon: <Icon name='dollar-sign' />,
        url: Location.COSTS,
        permission: locationUserPermission[Location.COSTS],
      },
      {
        title: 'Combustível',
        icon: <Icon name='fuel' />,
        url: Location.FUEL_CARD,
        permission: locationUserPermission[Location.FUEL_CARD],
      },
      {
        title: 'Manutenção',
        icon: <Icon name='wrench' />,
        url: Location.MAINTENANCE,
        redirect: Location.MAINTENANCE_HISTORY,
        permission: locationUserPermission[Location.MAINTENANCE],
      },
      {
        title: 'Relatórios',
        icon: <Icon name='chart-spline' />,
        url: Location.REPORTS_NEW,
        permission: locationUserPermission[Location.REPORTS],
        redirect: Location.REPORTS,
      },
      {
        title: 'Relatórios Avançados',
        icon: <Icon name='chart-no-axes-combined' />,
        url: Location.ADVANCED_ANALYTICS,
        permission: locationUserPermission[Location.ADVANCED_ANALYTICS],
        featureFlag: 'showAdvancedAnalyticsPage',
      },
    ],
  },

  {
    title: 'Minha frota',
    icon: <Icon name='truck' />,
    permission: UserPermission.BASIC_PERMISSION,
    section: [
      {
        title: 'Minha frota',
        icon: <Icon name='truck' />,
        open: true,
        permission: locationUserPermission[Location.VEHICLES],
        group: [
          {
            title: 'Veículos',
            url: Location.VEHICLES,
            permission: locationUserPermission[Location.VEHICLES],
          },
          {
            title: 'Grupos de Veículos',
            url: Location.VEHICLES_GROUPS,
            permission: locationUserPermission[Location.VEHICLES_GROUPS],
          },
          {
            title: 'Dispositivos',
            url: Location.DEVICES,
            permission: locationUserPermission[Location.DEVICES],
          },
          {
            title: 'Motoristas',
            url: Location.DRIVER_MANAGEMENT,
            permission: locationUserPermission[Location.DRIVER_MANAGEMENT],
          },
        ],
      },
      {
        title: 'Integrações',
        icon: <Icon name='workflow' />,
        permission: UserPermission.BASIC_PERMISSION,
        featureFlag: 'integrationsPage',
        group: [
          {
            title: 'Aplicativos',
            url: Location.APPS,
            permission: locationUserPermission[Location.APPS],
          },
          {
            title: 'APIs',
            url: Location.APIS,
            permission: locationUserPermission[Location.APIS],
          },
          {
            title: 'Webhooks',
            url: Location.WEBHOOKS,
            permission: locationUserPermission[Location.WEBHOOKS],
          },
        ],
      },
    ],
  },
];

export default sidebarDef;
